import React, { useEffect, useState } from 'react';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import '../App.css';

/**
 * SMS Opt-In Information Page
 * This component displays the SMS opt-in terms and conditions.
 * It can be accessed directly without authentication.
 * Uses the logged-in user's theme or the system default theme.
 */
function SmsOptIn() {
  const { applyTheme } = useTheme();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadTheme = async () => {
      try {
        const token = localStorage.getItem('token');

        if (token) {
          // User is logged in - get their theme preference
          try {
            const response = await api.get('/preferences');
            const userTheme = response.data.find(pref => pref.preference_key === 'theme');
            if (userTheme) {
              applyTheme(userTheme.preference_value);
            }
          } catch (error) {
            console.error('Error loading user theme:', error);
            // Fall back to default theme
            await loadDefaultTheme();
          }
        } else {
          // User not logged in - get system default theme
          await loadDefaultTheme();
        }
      } catch (error) {
        console.error('Error loading theme:', error);
      } finally {
        setLoading(false);
      }
    };

    const loadDefaultTheme = async () => {
      try {
        const response = await api.get('/admin/theme');
        if (response.data.defaultTheme) {
          applyTheme(response.data.defaultTheme);
        }
      } catch (error) {
        console.error('Error loading default theme:', error);
      }
    };

    loadTheme();
  }, [applyTheme]);

  if (loading) {
    return <div style={{ padding: '2rem', textAlign: 'center' }}>Loading...</div>;
  }

  return (
    <div style={{ minHeight: '100vh', backgroundColor: 'var(--color-background)', padding: '2rem' }}>
      <div style={{ maxWidth: '800px', margin: '0 auto', backgroundColor: 'var(--color-bg)', borderRadius: '8px', padding: '2rem', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <h1 style={{ marginBottom: '1.5rem', fontSize: '2rem', color: 'var(--color-primary)' }}>
            SMS Messaging Terms & Conditions
          </h1>

          <div style={{ lineHeight: '1.8', color: 'var(--color-text)' }}>
          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Overview
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              By providing your phone number and choosing SMS verification during registration,
              you agree to receive text messages from Brings to Mind for authentication and
              account security purposes.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              What You're Opting Into
            </h2>
            <p style={{ marginBottom: '0.5rem' }}>
              When you opt-in to SMS messaging with Brings to Mind, you consent to receive:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Verification Codes:</strong> 6-digit codes for account registration and login verification
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Account Security Notifications:</strong> Messages related to account security,
                such as phone number changes or account verification requests
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Password Reset Codes:</strong> One-time codes for password recovery (if you choose SMS-based recovery)
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                <strong>Reflection Reminders:</strong> Optional reminder messages to help you maintain your reflection practice
                (only if you enable this feature in your Notification settings)
              </li>
            </ul>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Message Frequency
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Message frequency varies based on your usage and preferences:
            </p>

            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', marginTop: '1rem', color: 'var(--color-heading)' }}>
              Security Messages (As Needed)
            </h3>
            <p style={{ marginBottom: '0.5rem' }}>
              You will receive security-related messages only when:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>You register a new account</li>
              <li style={{ marginBottom: '0.5rem' }}>You log in to your account</li>
              <li style={{ marginBottom: '0.5rem' }}>You request to change your phone number</li>
              <li style={{ marginBottom: '0.5rem' }}>You reset your password</li>
              <li style={{ marginBottom: '0.5rem' }}>You manually request a verification code resend</li>
            </ul>

            <h3 style={{ fontSize: '1.1rem', marginBottom: '0.5rem', marginTop: '1rem', color: 'var(--color-heading)' }}>
              Reflection Reminders (Optional, Configurable)
            </h3>
            <p style={{ marginBottom: '0.5rem' }}>
              If you enable reflection reminders in your Notification settings, you can configure the frequency
              to match your preferences:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}><strong>Daily:</strong> One reminder per day</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Weekly:</strong> One reminder per week</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Bi-weekly:</strong> One reminder every two weeks</li>
              <li style={{ marginBottom: '0.5rem' }}><strong>Monthly:</strong> One reminder per month</li>
            </ul>
            <p style={{ marginBottom: '0.5rem' }}>
              You have full control over whether you receive reminder messages and how often. Reminders
              can be enabled or disabled at any time in your account settings.
            </p>

            <p style={{ marginBottom: '1rem', marginTop: '1.5rem', padding: '1rem', background: 'var(--color-background-secondary)', borderRadius: '0.5rem' }}>
              <strong>Important:</strong> Brings to Mind does not send marketing messages, promotional
              content, or unsolicited communications via SMS. All messages are either transactional (security-related)
              or optional reminders that you explicitly enable.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Message & Data Rates
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Message and data rates may apply based on your mobile carrier's plan. Standard text
              messaging rates from your carrier will apply to all SMS messages sent and received.
              Brings to Mind does not charge for SMS messages, but your carrier may.
            </p>
            <p style={{ marginBottom: '1rem' }}>
              Please check with your mobile carrier for details about your messaging plan and any
              applicable charges.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Supported Carriers
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              SMS verification is supported on major U.S. carriers including AT&T, T-Mobile, Verizon,
              Sprint, and most other carriers. If you experience issues receiving messages, please
              contact your carrier to ensure SMS is enabled on your account.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              How to Opt-Out
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              You can opt-out of SMS messaging at any time by:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
              <li style={{ marginBottom: '0.5rem' }}>
                Changing your authentication method to email verification in your Account Settings
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Removing your phone number from your profile (you must have an email address on file)
              </li>
              <li style={{ marginBottom: '0.5rem' }}>
                Replying STOP to any verification message (note: this will disable SMS verification
                and you'll need to use email verification instead)
              </li>
            </ul>
            <p style={{ marginBottom: '1rem' }}>
              If you opt-out, you will no longer receive verification codes via SMS and must use
              email verification for account security. You can opt back in at any time by updating
              your preferences in Account Settings.
            </p>
          </section>

          <section style={{ marginBottom: '2rem' }}>
            <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
              Privacy & Data Protection
            </h2>
            <p style={{ marginBottom: '1rem' }}>
              Your phone number is stored securely and will never be:
            </p>
            <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>Shared with third parties for marketing purposes</li>
                <li style={{ marginBottom: '0.5rem' }}>Sold or rented to other companies</li>
                <li style={{ marginBottom: '0.5rem' }}>Used for unsolicited communications</li>
              </ul>
              <p style={{ marginBottom: '1rem' }}>
                We use Twilio, a trusted SMS service provider, to deliver verification codes.
                Your phone number is transmitted securely and is only used for the purposes described
                in this document. For more information about our data practices, please review our
                Privacy Policy.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
                Help & Support
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                For help with SMS verification or questions about these terms:
              </p>
              <ul style={{ marginLeft: '2rem', marginBottom: '1rem' }}>
                <li style={{ marginBottom: '0.5rem' }}>
                  Visit our Help Center (if logged in)
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Reply HELP to any verification message
                </li>
                <li style={{ marginBottom: '0.5rem' }}>
                  Contact support through your Account Settings
                </li>
              </ul>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
                Terms Updates
              </h2>
              <p style={{ marginBottom: '1rem' }}>
                We may update these SMS terms from time to time. Continued use of SMS verification
                after any changes indicates your acceptance of the new terms. We will notify you of
                any material changes via email or through your account dashboard.
              </p>
            </section>

            <section style={{ marginBottom: '2rem' }}>
              <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem', color: 'var(--color-heading)' }}>
                Agreement
              </h2>
              <p style={{ marginBottom: '1rem', fontWeight: 'bold' }}>
                By providing your phone number and selecting SMS verification during registration,
                you acknowledge that you have read, understood, and agree to these SMS Messaging
                Terms & Conditions.
              </p>
            </section>

          <div style={{ marginTop: '2rem', paddingTop: '2rem', borderTop: '1px solid var(--color-border)' }}>
            <p style={{ fontSize: '0.875rem', color: 'var(--color-text-light)' }}>
              Last Updated: December 3, 2025
            </p>
          </div>

          <div style={{ marginTop: '2rem', textAlign: 'center' }}>
            <a
              href="/"
              style={{
                padding: '0.75rem 2rem',
                background: 'var(--color-primary)',
                color: 'white',
                border: 'none',
                borderRadius: 'var(--radius-base, 0.5rem)',
                cursor: 'pointer',
                fontSize: '1rem',
                fontWeight: '500',
                textDecoration: 'none',
                display: 'inline-block'
              }}
            >
              Back to Main
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SmsOptIn;
